import {createAsyncThunk} from '@reduxjs/toolkit';
import {notification} from 'antd';

import {showMessageFromResponse} from 'shared/utils';
import {removeIsAuthenticatedFromStorage, setIsAuthenticatedToStorage} from 'shared/utils/storage';
import {authSliceActions} from 'store/slices/auth-slice/auth-slice';
import {
    authRequest,
    logoutRequest,
    changePasswordRequest,
    fetchTwoFACode,
    fetchTwoFAConfig,
    fetchUserInfo,
    IAuthRequestArgs,
    IChangePasswordRequestArgs,
    TFetchTwoFACodeArgs,
    fetchChangePasswordSettings,
    fetchAuthProfile,
    fetchAuthStatus,
    fetchMicroserviceToken,
    fetch2faCodeValidity,
    verify2fa,
} from 'store/slices/auth-slice/auth-slice-api';
import {
    AuthErrors, AuthResponseTypes, AUTH_SLICE, SESSION_EXPIRED_NOTIFICATION,
} from 'store/slices/auth-slice/auth-slice-constants';
import {
    EAuthProfile,
    TAuthResponseWithCredentials,
    IChangePasswordSettings,
    ITwoFactorAuthStatus,
    IUserInfo,
    IAuthResponse,
} from 'store/slices/auth-slice/auth-slice-types';

export const resetTokenInfo = () => {
    removeIsAuthenticatedFromStorage();
};

// todo: temporary solution
export const resetHiddenColumnsTokens = () => {
    Object.keys(localStorage)?.forEach(key => {
        if (key?.includes('-hidden-columns')) {
            localStorage.removeItem(key);
        }
    });
};

export const authorize = createAsyncThunk<
    TAuthResponseWithCredentials, IAuthRequestArgs, {rejectValue: TAuthResponseWithCredentials}
    >(`${AUTH_SLICE}/authorize`, async (args, {rejectWithValue}) => {
        const {username, isPortal2faAuth} = args;

        try {
            notification.close(SESSION_EXPIRED_NOTIFICATION.KEY);
            const authenticationResponse = await authRequest(args);

            if (authenticationResponse.code === 200) {
                    setIsAuthenticatedToStorage(true);
            } else {
                resetTokenInfo();
                return rejectWithValue({
                    ...authenticationResponse,
                    userName: username,
                });
            }

            return {
                ...authenticationResponse,
                userName: username,
                isPortal2faAuth
            };
        } catch (e) {
            resetTokenInfo();
            return rejectWithValue(e);
        }
    });

export const logout = createAsyncThunk(
    `${AUTH_SLICE}/logout`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await logoutRequest();
            resetTokenInfo();
            resetHiddenColumnsTokens();
            authSliceActions.resetMicroserviceToken();
            return response;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const loadUserInfo = createAsyncThunk<IUserInfo, void>(
    `${AUTH_SLICE}/loadUserInfo`,
    async (args, {dispatch, rejectWithValue}) => {
        try {
            const userInfoResponse = await fetchUserInfo();

            // todo - implement logout on userInfo fetch error
            if (userInfoResponse.status !== 200) {
                dispatch(logout());
            }

            return userInfoResponse.data;
        } catch (e) {
            showMessageFromResponse({
                response: e?.data?.message,
                isError: true,
                customMessage: AuthErrors.USER_INFO_ERROR_MESSAGE,
            });
            dispatch(logout());
            return rejectWithValue(e);
        }
    },
);

export const changePassword = createAsyncThunk<any, IChangePasswordRequestArgs, {rejectValue: string}>(
    `${AUTH_SLICE}/changePassword`,
    async (args, {rejectWithValue}) => {
        try {
            const changePasswordResponse = await changePasswordRequest(args);

            if (changePasswordResponse.status !== 200) {
                rejectWithValue(changePasswordResponse?.data?.message ?? AuthErrors.DEFAULT_ERROR_MESSAGE);
            }

            return changePasswordResponse;
        } catch (e) {
            return rejectWithValue(e?.response?.data?.message ?? AuthErrors.DEFAULT_ERROR_MESSAGE);
        }
    },
);

export const getTwoFAConfig = createAsyncThunk<string, void, {rejectValue: string}>(
    `${AUTH_SLICE}/getTwoFAConfig`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await fetchTwoFAConfig();
            if (response.status !== 200) {
                return rejectWithValue(response?.data?.message ?? AuthErrors.DEFAULT_ERROR_MESSAGE);
            }
            return response?.data;
        } catch (e) {
            return rejectWithValue(e?.response?.data?.message ?? AuthErrors.DEFAULT_ERROR_MESSAGE);
        }
    },
);

export const getTwoFACode = createAsyncThunk<ITwoFactorAuthStatus, TFetchTwoFACodeArgs, {rejectValue: void}>(
    `${AUTH_SLICE}/getTwoFACode`,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (args, _) => {
        try {
            const response = await fetchTwoFACode(args);
            if (response.status === 200) {
                return {message: response?.data?.message, isError: false};
            }
            return {message: AuthErrors.DEFAULT_ERROR_MESSAGE, isError: true};
        } catch (e) {
            console.error(e);
            return {message: e?.response?.data?.message, isError: true};
        }
    },
);

export const get2faCodeValidity = createAsyncThunk<number, void, {rejectValue: string}>(
    `${AUTH_SLICE}/get2faCodeValidity`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await fetch2faCodeValidity();
            if (response.status !== 200) {
                return rejectWithValue(response?.data);
            }

            return response?.data;
        } catch (e) {
            return rejectWithValue(e?.response?.data);
        }
    },
);

export const check2faCode = createAsyncThunk<IAuthResponse, {twoFactorAuthCode?: string}, {rejectValue: IAuthResponse}>(
    `${AUTH_SLICE}/check2faCode`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await verify2fa({twoFactorAuthCode: args?.twoFactorAuthCode});
            if (response.status !== 200) {
                return rejectWithValue(response?.data);
            }

            setIsAuthenticatedToStorage(true);

            return response?.data;
        } catch (e) {
            resetTokenInfo();
            return rejectWithValue(e?.response?.data);
        }
    },
);

export const getChangePasswordSettings = createAsyncThunk<IChangePasswordSettings, void, {rejectValue: string}>(
    `${AUTH_SLICE}/getChangePasswordSettings`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await fetchChangePasswordSettings();
            if (response.status !== 200) {
                return rejectWithValue(AuthErrors.CHANGE_PASSWORD_SETTINGS_REQUEST_FAILED);
            }
            return response?.data;
        } catch (e) {
            return rejectWithValue(AuthErrors.CHANGE_PASSWORD_SETTINGS_REQUEST_FAILED);
        }
    },
);

export const getAuthProfile = createAsyncThunk<EAuthProfile, void, {rejectValue: string}>(
    `${AUTH_SLICE}/getAuthProfile`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await fetchAuthProfile();
            if (response.status !== 200) {
                return rejectWithValue(AuthErrors.PROFILE_NOT_FOUND);
            }
            return response?.data;
        } catch (e) {

            const isPasswordExpired = e?.response?.data?.type === AuthResponseTypes.PASSWORD_EXPIRED;
            return rejectWithValue(isPasswordExpired ? AuthErrors.PASSWORD_EXPIRED : AuthErrors.PROFILE_NOT_FOUND);
        }
    },
);

export const checkAuthStatus = createAsyncThunk<string, void, {rejectValue: string | void}>(
    `${AUTH_SLICE}/checkAuthStatus`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await fetchAuthStatus();

            if (response.status !== 200) {
                return rejectWithValue(response?.data); // data has redirect-url
            }

            if (response?.data) {
                setIsAuthenticatedToStorage(true);
            }
            
            return response?.data;
        } catch (e) {
            return rejectWithValue(e?.response?.data);
        }
    },
);

export const loadMicroserviceToken = createAsyncThunk<string, void>(
    `${AUTH_SLICE}/loadMicroserviceToken`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await fetchMicroserviceToken();

            return response.data;
        } catch (e) {
            showMessageFromResponse({
                response: e?.data?.message,
                isError: true,
                customMessage: AuthErrors.MICROSERVICE_TOKEN_ERROR,
            });
            return rejectWithValue(AuthErrors.MICROSERVICE_TOKEN_ERROR);
        }
    },
);
